// VERIFY-01 (B11) — Kernvel-staff CROSS-SHOP verification view (never in the nav, never an operator session).
//
// The operator's /verification page is scoped to their own shop by their console JWT and stays that way.
// This page lists every shop's persisted reconciliation runs side by side (filter to one shop or a status)
// and opens any run's immutable findings. It talks ONLY to /api/v1/staff with a Kernvel staff token
// holding verify01.read (StaffGate); an operator token is refused by the backend. Every read is audited
// server-side. Read only: runs are started by the CLI or the daily beat. Backend: app/api/staff/verification.py.
import { useCallback, useEffect, useState } from "react";

import { staffErrorText, staffGet } from "../staff";
import StaffGate from "./StaffGate";
import { Counts, RunDetailView, runStatusClass, type RunGetter, type VerificationRun } from "./Verification";

export interface StaffRun extends VerificationRun {
  shop_id: string;
  shopify_domain: string | null;
  shop_name: string | null;
}

interface StaffRunList {
  scope: "all_shops" | "one_shop";
  runs: StaffRun[];
  has_more: boolean;
  next_offset: number | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const STATUSES = ["", "completed", "running", "failed", "abandoned"] as const;

function StaffVerificationBody() {
  const [shopFilter, setShopFilter] = useState("");
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("");
  const [list, setList] = useState<StaffRunList | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (offset: number) => {
    const params = new URLSearchParams({ limit: "50" });
    const shop = shopFilter.trim();
    if (shop && UUID.test(shop)) params.set("shop_id", shop);
    if (status) params.set("status", status);
    if (offset) params.set("offset", String(offset));
    try {
      const page = await staffGet<StaffRunList>(`/verification/runs?${params.toString()}`);
      setList((prev) => (offset && prev ? { ...page, runs: [...prev.runs, ...page.runs] } : page));
      setErr(null);
    } catch (e) {
      setErr(staffErrorText(e, "Could not list verification runs"));
    }
  }, [shopFilter, status]);

  useEffect(() => {
    void load(0);
  }, [load]);

  const shopInvalid = shopFilter.trim() !== "" && !UUID.test(shopFilter.trim());
  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <label>
          Shop ID (blank = every shop)
          <input value={shopFilter} onChange={(e) => setShopFilter(e.target.value)} style={{ display: "block", width: "100%" }} />
        </label>
        {shopInvalid && <p className="risk-sub">Enter a shop UUID, or leave it blank for every shop.</p>}
        <label>
          Status
          <select aria-label="Run status" value={status} onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}>
            {STATUSES.map((value) => <option key={value} value={value}>{value || "any"}</option>)}
          </select>
        </label>
      </div>
      {err && <div className="banner-err">{err}</div>}
      {list && (
        <section className="lane">
          <div className="lane-head">
            <span>{list.scope === "all_shops" ? "Runs across every shop" : "Runs for one shop"}</span>
            <span className="lane-count">{list.runs.length}{list.has_more ? "+" : ""}</span>
          </div>
          <div className="lane-body">
            {list.runs.length === 0 ? <p className="lane-empty">No verification runs match.</p> : list.runs.map((run) => (
              <button key={run.run_id} className={`verify-run ${runStatusClass(run)}`} onClick={() => setSelected(run.run_id)}
                aria-pressed={selected === run.run_id}>
                <span className="dlq-event">{run.shop_name ?? "unknown shop"} · {run.shopify_domain ?? run.shop_id}</span>
                <span className="dlq-sub">{run.status} · started {run.started_at}</span>
                <Counts run={run} />
              </button>
            ))}
            {list.has_more && list.next_offset !== null && (
              <button className="dlq-tab" onClick={() => void load(list.next_offset ?? 0)}>Load more runs</button>
            )}
          </div>
        </section>
      )}
      {selected && <RunDetailView key={selected} runId={selected} get={staffGet as RunGetter} />}
    </>
  );
}

export default function StaffVerification() {
  return (
    <StaffGate title="Verification (all shops)" scope="verify01.read">
      {() => <StaffVerificationBody />}
    </StaffGate>
  );
}
